import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface LoadingScreenProps {
  onComplete: () => void;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(() => onComplete(), 600);
          return 100;
        }
        return prev + 2;
      });
    }, 40);

    const textTimer = setTimeout(() => setShowText(true), 200);

    return () => {
      clearInterval(timer);
      clearTimeout(textTimer);
    };
  }, [onComplete]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed inset-0 z-[10000] bg-parchment flex items-center justify-center"
      >
        {/* Subtle floating leaves */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              initial={{ x: `${15 + i * 14}vw`, y: '-5vh', opacity: 0, rotate: Math.random() * 30 - 15 }}
              animate={{ y: '110vh', opacity: [0, 0.4, 0.3, 0], rotate: [0, 20, -10, 15] }}
              transition={{
                duration: 10 + i * 1.5,
                repeat: Infinity,
                delay: i * 1.2,
                ease: 'easeInOut',
              }}
            >
              <svg width="14" height="22" viewBox="0 0 14 22" fill="none">
                <path d="M7 1C11 4 13 9 7 21C1 9 3 4 7 1Z" fill="#4A7C59" opacity="0.5" />
                <line x1="7" y1="1" x2="7" y2="21" stroke="#4A7C59" strokeWidth="0.5" opacity="0.4" />
              </svg>
            </motion.div>
          ))}
        </div>

        <div className="relative z-10 text-center px-8">
          {/* Logo */}
          <motion.div
            initial={{ scale: 0.75, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.9, ease: 'backOut' }}
            className="mb-8"
          >
            <img
              src="/nestbloom-logo.png"
              alt="NestBloom"
              className="h-32 object-contain mx-auto"
            />
          </motion.div>

          <AnimatePresence>
            {showText && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-8"
              >
                <p className="text-sm text-warm-gray font-italic italic tracking-widest">
                  Herbal Urban Fragrance
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Progress bar */}
          <div className="w-56 mx-auto">
            <div className="w-full bg-linen rounded-full h-1">
              <motion.div
                className="bg-herb-green h-1 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.2 }}
              />
            </div>
            <motion.p
              className="text-xs text-warm-gray/70 mt-3 tracking-wide"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {progress < 40 ? 'Blending your space…' :
               progress < 75 ? 'Pouring with care…' :
               'Almost ready…'}
            </motion.p>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LoadingScreen;
